/**
 * Admin — /admin/grants. Mirrors the pattern of /admin/partners:
 * list at top, active/inactive filter, inline "edit / deactivate"
 * actions per row, and a create modal for new rows.
 *
 * Phase G1 seed rows have `created_by_admin_id: null` and are marked
 * as "Seeded" in the row meta. Admin edits to a seeded row are safe —
 * the boot-time seed script only bumps `source_verified_at` on
 * existing rows (never overwrites program terms or amounts).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Save, Trash2, ExternalLink, Sprout, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";

const DIFFICULTY_OPTIONS = ["easy", "medium", "hard"];
const DEADLINE_TYPE_OPTIONS = ["rolling", "fixed", "annual"];

function money(n) {
  if (n == null) return "—";
  return `$${Number(n).toLocaleString()}`;
}

function StatusPill({ deadline_status }) {
  const meta = {
    open: { label: "Open", color: "#34D399" },
    rolling: { label: "Rolling", color: "#A78BFA" },
    upcoming: { label: "Upcoming", color: "#60A5FA" },
    closed: { label: "Closed", color: "#EF4444" },
    unknown: { label: "Unknown", color: "#94A3B8" },
  }[deadline_status || "unknown"];
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold"
      style={{ background: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}44` }}
    >
      {meta.label}
    </span>
  );
}

function EditRow({ row, onSave, onDeactivate }) {
  const [form, setForm] = useState(() => ({ ...row }));
  const [saving, setSaving] = useState(false);

  function upd(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit() {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        agency: form.agency,
        program_summary: form.program_summary,
        award_amount_min: form.award_amount_min == null ? null : Number(form.award_amount_min),
        award_amount_max: form.award_amount_max == null ? null : Number(form.award_amount_max),
        deadline_type: form.deadline_type,
        difficulty: form.difficulty,
        estimated_completion_hours: form.estimated_completion_hours == null ? null : Number(form.estimated_completion_hours),
        official_application_url: form.official_application_url,
        tags: form.tags,
        required_documents: form.required_documents,
        source_url: form.source_url,
        source_verified_at: form.source_verified_at,
        active: form.active,
      };
      const res = await api.patch(`/admin/grants/${row.id}`, payload);
      onSave(res.data);
      toast.success("Grant updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <tr
      data-testid={`admin-grants-edit-row-${row.id}`}
      style={{ background: "rgba(167,139,250,0.06)" }}
    >
      <td colSpan={6} className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-xs text-v2-textMuted">Title
            <input value={form.title ?? ""} onChange={(e) => upd("title", e.target.value)} className="mt-1 block w-full rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }} data-testid={`admin-grants-edit-title-${row.id}`} />
          </label>
          <label className="text-xs text-v2-textMuted">Agency
            <input value={form.agency ?? ""} onChange={(e) => upd("agency", e.target.value)} className="mt-1 block w-full rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }} data-testid={`admin-grants-edit-agency-${row.id}`} />
          </label>
          <label className="text-xs text-v2-textMuted col-span-full">Program summary
            <textarea rows={3} value={form.program_summary ?? ""} onChange={(e) => upd("program_summary", e.target.value)} className="mt-1 block w-full rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }} data-testid={`admin-grants-edit-summary-${row.id}`} />
          </label>
          <label className="text-xs text-v2-textMuted">Award min
            <input type="number" value={form.award_amount_min ?? ""} onChange={(e) => upd("award_amount_min", e.target.value === "" ? null : Number(e.target.value))} className="mt-1 block w-full rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }} />
          </label>
          <label className="text-xs text-v2-textMuted">Award max
            <input type="number" value={form.award_amount_max ?? ""} onChange={(e) => upd("award_amount_max", e.target.value === "" ? null : Number(e.target.value))} className="mt-1 block w-full rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }} />
          </label>
          <label className="text-xs text-v2-textMuted">Deadline type
            <select value={form.deadline_type} onChange={(e) => upd("deadline_type", e.target.value)} className="mt-1 block w-full rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }}>
              {DEADLINE_TYPE_OPTIONS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </label>
          <label className="text-xs text-v2-textMuted">Difficulty
            <select value={form.difficulty} onChange={(e) => upd("difficulty", e.target.value)} className="mt-1 block w-full rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }}>
              {DIFFICULTY_OPTIONS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </label>
          <label className="text-xs text-v2-textMuted">Source URL
            <input value={form.source_url ?? ""} onChange={(e) => upd("source_url", e.target.value)} className="mt-1 block w-full rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }} data-testid={`admin-grants-edit-source-${row.id}`} />
          </label>
          <label className="text-xs text-v2-textMuted">Source verified at (ISO)
            <input value={form.source_verified_at ?? ""} onChange={(e) => upd("source_verified_at", e.target.value)} placeholder="2026-07-18T00:00:00+00:00" className="mt-1 block w-full rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }} data-testid={`admin-grants-edit-verified-${row.id}`} />
          </label>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid={`admin-grants-edit-save-${row.id}`}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-v2-background bg-v2-accent hover:bg-white disabled:opacity-60"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={() => onDeactivate(row.id)}
            data-testid={`admin-grants-deactivate-${row.id}`}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-rose-300 hover:text-rose-100"
            style={{ background: "transparent", border: "1px solid rgba(239,68,68,0.45)" }}
          >
            <Trash2 className="w-3.5 h-3.5" /> Deactivate
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminGrants() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // row id currently being edited
  const [creating, setCreating] = useState(false);
  const [newRow, setNewRow] = useState({
    title: "", agency: "", program_summary: "",
    official_application_url: "", source_url: "",
    deadline_type: "rolling", difficulty: "medium",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/grants");
      setRows(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function submitCreate() {
    try {
      const res = await api.post("/admin/grants", newRow);
      setRows((prev) => [res.data, ...prev]);
      setCreating(false);
      setNewRow({
        title: "", agency: "", program_summary: "",
        official_application_url: "", source_url: "",
        deadline_type: "rolling", difficulty: "medium",
      });
      toast.success("Grant created.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function deactivate(id) {
    try {
      await api.delete(`/admin/grants/${id}`);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, active: false } : r)));
      setExpanded(null);
      toast.success("Grant deactivated.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <AdminLayout
      title="Grants"
      subtitle="Manage the Grant Navigator™ catalog surfaced at /grants."
      actions={
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="admin-grants-create-btn"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-v2-background bg-v2-accent hover:bg-white"
        >
          <Plus className="w-4 h-4" /> New grant
        </button>
      }
    >
    <div data-testid="admin-grants-root" className="max-w-7xl mx-auto">
      <header className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm text-v2-textMuted">
            Seeded rows carry a Seed indicator and can be edited freely — the
            boot seed only bumps <code className="mx-1 text-xs">source_verified_at</code> on existing rows.
          </p>
        </div>
      </header>

      {creating && (
        <section
          data-testid="admin-grants-create-panel"
          className="mt-6 rounded-2xl p-6"
          style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}
        >
          <p className="text-xs uppercase tracking-[0.28em] text-v2-textMuted">New grant</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Title" data-testid="admin-grants-create-title" value={newRow.title} onChange={(e) => setNewRow((r) => ({ ...r, title: e.target.value }))} className="rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }} />
            <input placeholder="Agency" data-testid="admin-grants-create-agency" value={newRow.agency} onChange={(e) => setNewRow((r) => ({ ...r, agency: e.target.value }))} className="rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }} />
            <textarea placeholder="Program summary (educational, no guarantees)" rows={3} value={newRow.program_summary} onChange={(e) => setNewRow((r) => ({ ...r, program_summary: e.target.value }))} className="md:col-span-2 rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }} />
            <input placeholder="Application URL" value={newRow.official_application_url} onChange={(e) => setNewRow((r) => ({ ...r, official_application_url: e.target.value }))} className="rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }} />
            <input placeholder="Source URL" value={newRow.source_url} onChange={(e) => setNewRow((r) => ({ ...r, source_url: e.target.value }))} className="rounded-xl px-3 py-2 text-sm text-v2-text" style={{ background: "var(--v2-background)", border: "1px solid var(--v2-border)" }} />
          </div>
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={submitCreate} data-testid="admin-grants-create-submit" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-v2-background bg-v2-accent hover:bg-white">
              <Save className="w-3.5 h-3.5" /> Create
            </button>
            <button type="button" onClick={() => setCreating(false)} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-v2-textMuted">
              Cancel
            </button>
          </div>
        </section>
      )}

      {loading && <p className="mt-6 text-sm text-v2-textMuted">Loading…</p>}

      {!loading && (
        <section
          className="mt-6 rounded-2xl overflow-hidden"
          style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}
        >
          <table className="w-full text-sm text-v2-text">
            <thead className="text-[10px] uppercase tracking-[0.14em] text-v2-textMuted">
              <tr>
                <th className="text-left px-5 py-3">Title</th>
                <th className="text-left px-5 py-3">Agency</th>
                <th className="text-left px-5 py-3">Amount</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Verified</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <>
                  <tr
                    key={r.id}
                    data-testid={`admin-grants-row-${r.id}`}
                    className="border-t"
                    style={{ borderColor: "var(--v2-border)" }}
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className={r.active ? "" : "text-v2-textMuted line-through"}>{r.title}</span>
                        {!r.created_by_admin_id && (
                          <span
                            title="Seeded row — boot-time seed only bumps verified date."
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] text-v2-textMuted"
                          >
                            <Sprout className="w-3 h-3" /> Seed
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-v2-textMuted">{r.agency}</td>
                    <td className="px-5 py-3 text-v2-textMuted">{money(r.award_amount_min)} – {money(r.award_amount_max)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <StatusPill deadline_status={r.deadline_status} />
                        {r.active && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-xs text-v2-textMuted">
                      {r.source_verified_at ? new Date(r.source_verified_at).toISOString().slice(0, 10) : "—"}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <a
                        href={r.official_application_url || "#"}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="mr-3 inline-flex items-center gap-1 text-xs text-v2-textMuted hover:text-v2-accent"
                      >
                        Source <ExternalLink className="w-3 h-3" />
                      </a>
                      <button
                        type="button"
                        onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                        data-testid={`admin-grants-edit-toggle-${r.id}`}
                        className="text-xs font-semibold text-v2-accent hover:text-white"
                      >
                        {expanded === r.id ? "Close" : "Edit"}
                      </button>
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <EditRow
                      row={r}
                      onSave={(next) => {
                        setRows((prev) => prev.map((row) => (row.id === next.id ? next : row)));
                        setExpanded(null);
                      }}
                      onDeactivate={deactivate}
                    />
                  )}
                </>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
    </AdminLayout>
  );
}
